import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { loanFormSchema, LoanFormValues } from "./schemas/loanForm.schema";
import {
  useCreateSaccoLoan,
  useUpdateSaccoLoan,
} from "@saccoHooks";
import {SaccoLoan} from "@/@types/sacco.models.ts";

interface LoanFormProps {
  membershipId: string;
  nativeCurrency?: string;
  loan?: SaccoLoan;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function LoanForm({
  membershipId,
  nativeCurrency = 'KES',
  loan,
  open,
  onOpenChange,
  onSuccess,
}: LoanFormProps) {
  const isEdit = !!loan;
  const canEdit = !isEdit || loan.status === "active";

  const createLoan = useCreateSaccoLoan(membershipId);
  const updateLoan = useUpdateSaccoLoan(membershipId);

  const form = useForm<LoanFormValues>({
    resolver: zodResolver(loanFormSchema),
    defaultValues: {
      principal: "" as unknown as number,
      annual_interest_rate: "" as unknown as number,
      total_installments: "" as unknown as number,
      disbursement_date: "",
      note: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        principal: loan?.principal ? Number(loan.principal) : ("" as unknown as number),
        annual_interest_rate: loan?.annual_interest_rate
          ? Number(loan.annual_interest_rate)
          : ("" as unknown as number),
        total_installments: loan?.total_installments
          ? Number(loan.total_installments)
          : ("" as unknown as number),
        disbursement_date: loan?.disbursement_date ?? "",
        note: loan?.note ?? "",
      });
    }
  }, [open, loan, form]);

  const isPending = createLoan.isPending || updateLoan.isPending;

  async function onSubmit(values: LoanFormValues) {
    try {
      if (isEdit) {
        await updateLoan.mutateAsync({
          id: loan.id,
          principal: values.principal,
          annual_interest_rate: values.annual_interest_rate,
          total_installments: values.total_installments,
          disbursement_date: values.disbursement_date,
          note: values.note || null,
        });
        toast.success("Loan updated successfully");
      } else {
        await createLoan.mutateAsync({
          principal: values.principal,
          annual_interest_rate: values.annual_interest_rate,
          total_installments: values.total_installments,
          disbursement_date: values.disbursement_date,
          note: values.note || null,
        });
        toast.success("Loan created successfully");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(isEdit ? "Failed to update loan" : "Failed to create loan");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Loan" : "Add Loan"}
            {isEdit && loan.status !== "active" && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                (read-only — loan is {loan.status})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="principal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{`Principal (${nativeCurrency}) *`}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        disabled={!canEdit}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="annual_interest_rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Annual Interest Rate (%) *</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        disabled={!canEdit}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="total_installments"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Total Installments *</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="12"
                        disabled={!canEdit}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="disbursement_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Disbursement Date *</FormLabel>
                    <FormControl>
                      <Input type="date" disabled={!canEdit} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional note"
                      className="resize-none"
                      rows={3}
                      disabled={!canEdit}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              {canEdit && (
                <Button type="submit" disabled={isPending}>
                  {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
